import { Button } from "@pepperstone/pepper-ui-kit";
import { useMemo, useState } from "react";

import type { DropdownOption } from "../../../DropDownV2";
import type { ClientAccountTableFilterSubProps } from "./types";

import { Button as ButtonV2 } from "../../../Button";
import { FilterIcon } from "../../../Icons";
import { SearchInput } from "../../../SearchInput";
import { Typo } from "../../../Typo";
import { useClientAccountReportContext } from "../providers/AccountReportProvider";
import { ClientAccountFilterModal } from "./FilterModal";

export const DesktopFilters = ({
  handleExportButton,
  handleSearchInput,
  handleRebateAccountsDropdown,
  handleRebateProgramsMultiSelect,
  canViewPII,
  redactedFields,
  translations,
  showRebateProgramFilter,
}: ClientAccountTableFilterSubProps) => {
  const { translatedContent } = translations;
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const {
    state: {
      searchQuery,
      selected: {
        rebateAccountIDs: selectedRebateAccountIDs,
        rebateProgramIDs: selectedRebateProgramIDs,
      },
    },
    options: { rebateAccountIDs, rebateProgramIDs },
  } = useClientAccountReportContext();

  const [search, setSearch] = useState(searchQuery);

  const selectedRebateAccounts = useMemo(() => {
    return ((selectedRebateAccountIDs as string[]) ?? []).map((id) => ({
      label:
        rebateAccountIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateAccountIDs, selectedRebateAccountIDs]);

  const selectedRebatePrograms = useMemo(() => {
    return ((selectedRebateProgramIDs as string[]) ?? []).map((id) => ({
      label:
        rebateProgramIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateProgramIDs, selectedRebateProgramIDs]);

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleRebateAccountsDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  // Rebate account and rebate program default to no selection -- an
  // unselected axis means "no restriction, show full IB data" rather than an
  // implicit "all", so it only counts once the user actually picks something
  // (and the two are mutually exclusive, so at most one of them ever
  // contributes). Account Report has no date filter, unlike Trading Report.
  const filterCount =
    (selectedRebateAccountIDs &&
    (selectedRebateAccountIDs as string[]).length > 0
      ? 1
      : 0) +
    (showRebateProgramFilter &&
    selectedRebateProgramIDs &&
    (selectedRebateProgramIDs as string[]).length > 0
      ? 1
      : 0);

  const searchTitle = useMemo(() => {
    if (canViewPII) {
      if (redactedFields?.includes("name")) {
        return translatedContent?.filters?.searchWithPIIEmail;
      }
      if (redactedFields?.includes("email")) {
        return translatedContent?.filters?.searchWithPIIName;
      }
      return translatedContent?.filters?.search;
    }
    return translatedContent?.filters?.searchNoPII;
  }, [canViewPII, redactedFields, translatedContent]);

  return (
    <div data-testid="desktop-filters" className="mt-10 flex flex-col">
      <div className="flex grow flex-row items-center gap-4">
        <div className="w-full max-w-[440px]">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder={searchTitle}
            inputClassName="!outline-0 hover:!outline-0 focus:!outline-2 focus:!outline-stroke-focused"
          />
        </div>
        <ButtonV2
          variant="tonal"
          size="md"
          onClick={() => setIsFilterOpen(true)}
        >
          <span className="flex items-center gap-2">
            <FilterIcon
              className="h-4 w-4"
              aria-hidden="true"
              focusable="false"
            />
            <Typo element="span" type="labelSM">
              {translatedContent.filters?.filterButton ?? "Filter"}
            </Typo>
            {filterCount > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-brand-default px-1.5">
                <Typo
                  element="span"
                  type="label2XS"
                  textColor="text-text-inverse-primary"
                >
                  {filterCount}
                </Typo>
              </span>
            )}
          </span>
        </ButtonV2>
        <div className="ml-auto">
          <Button
            as="button"
            type="button"
            size="ps-btn-md"
            variant="ps-btn-secondary"
            onClick={handleExportButton}
          >
            {translatedContent.exportCSVText}
          </Button>
        </div>
      </div>

      <ClientAccountFilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        rebateAccountOptions={rebateAccountIDs}
        selectedRebateAccounts={selectedRebateAccounts}
        onRebateAccountsChange={handleDropdownChange}
        rebateProgramOptions={rebateProgramIDs}
        selectedRebatePrograms={selectedRebatePrograms}
        onRebateProgramsChange={(options) =>
          handleRebateProgramsMultiSelect?.(options)
        }
        translations={translations}
        showRebateProgramFilter={showRebateProgramFilter}
      />
    </div>
  );
};
